import _ from 'lodash'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { Grid, Stack, Typography } from '@mui/material'

import { useGetExpertiseQuery, useUpdateExpertiseMutation } from '@/core/services/expertise'

import { getApiErrorMessage } from '@/utils/errors'

import AddHealthDataButton from '../components/AddHealthDataButton'
import ExpertiseConsultationDialog from './ExpertiseConsultationDialog'
import ExpertiseConsultationsDialog from './ExpertiseConsultationsDialog'
import HealthData from '../components/HealthData'

type HealthDataListProps = {
  readOnly?: boolean
  showTitle?: boolean
  id?: string
  expertiseId?: string
}

export default function HealthDataList(props: HealthDataListProps) {
  const { readOnly = false, showTitle = true, id } = props
  const { expertiseId: paramExpertiseId } = useParams()
  const expertiseId = props.expertiseId || paramExpertiseId
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [consultationsDialogOpen, setConsultationsDialogOpen] = useState<boolean>(false)
  const [selectedConsultationId, setSelectedConsultationId] = useState<string | undefined>(undefined)

  const { data: expertise } = useGetExpertiseQuery(expertiseId ?? skipToken)
  const [updateExpertise] = useUpdateExpertiseMutation()

  const handleAddConsultationMesures = async (consultationId: string) => {
    if (!expertise) return

    try {
      await updateExpertise({ id: expertise._id, consultations: _.map(expertise.consultations, '_id').concat(consultationId) }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
    }
  }

  const handleDeleteConsultationMesures = async (consultationId: string) => {
    if (!expertise) return

    try {
      const consultations = _.map(
        _.filter(expertise.consultations, (consultation) => consultation._id !== consultationId),
        '_id'
      )
      await updateExpertise({ id: expertise._id, consultations }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
    }
  }

  const handleOpenExpertiseConsultationsDialog = () => {
    setConsultationsDialogOpen(true)
  }

  const handleCloseExpertiseConsultationsDialog = () => {
    setSelectedConsultationId(undefined)
    setConsultationsDialogOpen(false)
  }

  const handleOpenExpertiseConsultationDialog = (consultationId: string) => {
    setSelectedConsultationId(consultationId)
  }

  const handleCloseExpertiseConsultationDialog = () => {
    setSelectedConsultationId(undefined)
  }

  return (
    <>
      <Stack direction="column" spacing={2} id={id}>
        {showTitle && (
          <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
            {t('page.expertise.healthData.title')}
          </Typography>
        )}
        <div>
          <Grid container spacing={3}>
            {!readOnly && (
              <Grid item xs={6} md={4} lg={3} xl={2}>
                <AddHealthDataButton onClick={handleOpenExpertiseConsultationsDialog} />
              </Grid>
            )}
            {expertise?.consultations?.map((consultation) => (
              <Grid key={consultation._id} item xs={6} md={4} lg={3} xl={2}>
                <HealthData consultationId={consultation._id} date={consultation.startDate} onClick={handleOpenExpertiseConsultationDialog} />
              </Grid>
            ))}
          </Grid>
        </div>
      </Stack>
      <ExpertiseConsultationsDialog
        onAdd={handleAddConsultationMesures}
        onClose={handleCloseExpertiseConsultationsDialog}
        onDelete={handleDeleteConsultationMesures}
        onShow={handleOpenExpertiseConsultationDialog}
        open={consultationsDialogOpen}
      />
      <ExpertiseConsultationDialog
        consultationId={selectedConsultationId}
        onClose={handleCloseExpertiseConsultationDialog}
        open={!!selectedConsultationId}
        readOnly={readOnly}
      />
    </>
  )
}
